/**
 * U5 (LTX / Hunyuan / PixVerse): the resolution SPELLING CORPUS and the legacy classifiers the
 * U5 descriptors are derived from.
 *
 * PURE: `import type` + sibling modules only (purity.test). Importable from server/ and src/.
 * Owned by U5; imported only by fal/ltx.ts, fal/hunyuan.ts and fal/pixverse.ts.
 *
 * WHY THIS FILE EXISTS (review round 1, defects 1/2/4)
 * ---------------------------------------------------
 * Legacy classifies a resolution with SUBSTRING rules — `res.includes('2160')`,
 * `res.toLowerCase().includes('4k')`, and `getResolutionTier`'s whole `includes()` ladder — over
 * the RAW string the caller sent. The headless REST API forwards that string untouched, so the
 * domain is every string, not a dropdown.
 *
 * The frozen descriptor vocabulary (models/types.ts, FROZEN at U1a) has no substring classifier:
 * `Param.aliases` is a finite map, `Param.values` a finite list, `Cond.in` a finite list and
 * `Rate.table` a finite table. Three of the U5 rules are therefore expressed in ways that ARE
 * total over every string, and one is not:
 *
 *   ltx-2 / ltx-2-fast   TOTAL. Price is the generic ladder, which reads the raw value, so the
 *                        param is `invalid: 'passthrough'` and the ladder classifies. The editor
 *                        payload rule is exact-match with an else-branch, which two ordered
 *                        `legacy.wire` entries (constant, then exact map) reproduce for EVERY
 *                        string. See ltx.ts.
 *   ltx-2.3-fast         TOTAL. `canonicalLtx23FastResolution` is trim+lowercase+exact-match,
 *                        which `normalize: 'lower'` + aliases + coerce-to-default reproduces.
 *   pixverse-*           TOTAL. Ladder over the raw value (passthrough) + an exact wire rule.
 *   ltx-2.3 (Pro)        NOT TOTAL. Its price table (37/74/148) is not the ladder's curve, so it
 *   hunyuan-1.5          must be keyed on a canonical value; and Hunyuan's editor refusal is
 *                        "ladder multiplier > 1", which no `invalid` policy can express.
 *
 * For those two, `RESOLUTION_SPELLINGS` below is the verified domain and the classifiers are
 * applied to it AT MODULE LOAD to DERIVE the alias maps — the descriptor is never hand-fitted to
 * whatever the U0 edge fixtures happen to contain. Every entry it produces is then checked
 * against the real `CreditService.calculateCost` by pricingParity (pricingHarness.pricingDomain
 * unions `values ∪ uiValues ∪ aliases` into the domain it compares), and the residual — strings
 * OUTSIDE the corpus that the substring rules would classify differently — is enumerated and
 * pinned by `*.parity.test.ts` ('the residual of the finite alias domain is exactly ...').
 * Closing the residual needs one additive `Param` rule in types.ts; see the U5 report.
 */
import { getResolutionTier, legacyResolutionMultiplier } from '../pricing';

/**
 * Every resolution spelling U5 claims to reproduce exactly.
 *
 * Built from parts rather than listed, so it cannot quietly become "the spellings the fixtures
 * use": the tokens are every tier name and bare number the repo, the nodes, fal and the U0 edge
 * corpus use; the shapes are the WxH / marketing / frame-rate forms a headless caller sends; and
 * each is taken with leading, trailing and both-side whitespace (legacy trims nothing on these
 * paths, so ' 4k' and '4k ' really are different inputs).
 */
const TOKENS: readonly string[] = [
  '4k', '4K', '2160', '2160p', '2160P', '2160i', '1440', '1440p', '1440P', '1080', '1080p', '1080P',
  '720', '720p', '720P', '580p', '480', '480p', '480P', '360p', '2k', '2K', '1k', '512px', '1024px',
  '2048px', '4096px', 'uhd', 'UHD', 'qhd', 'QHD', 'fhd', 'FHD', 'hd', 'HD', '', 'garbage', 'auto', 'best',
];

const SHAPES: readonly string[] = [
  '3840x2160', '2160x3840', '1920x1080', '1080x1920', '2560x1440', '1440x2560', '1280x720', '720x1280',
  '1792x1024', '1024x1792', '854x480', '480x854', '4K UHD', 'UHD-4K', '4k uhd', 'uhd-4k', '4kuhd',
  '2160p60', '2160p30', '2160P60', '4K60', '4k60', '1080p60', '1080p30', '1080P60', '720p60',
  '3840X2160', '1920X1080', '4K/60', '4k_60fps', 'UHD 2160',
];

function pad(values: readonly string[]): string[] {
  const out = new Set<string>();
  for (const v of values) {
    out.add(v);
    out.add(` ${v}`);
    out.add(`${v} `);
    out.add(` ${v} `);
  }
  return [...out];
}

export const RESOLUTION_SPELLINGS: readonly string[] = Object.freeze(pad([...TOKENS, ...SHAPES]));

// ---------------------------------------------------------------------------
// LTX-2.3 Pro: the three legacy readings of one raw string, transcribed once
// ---------------------------------------------------------------------------

/** CreditService.ts:1354 (`resKey`). `resolution || '1080p'` makes absent/'' land here as '1080p'. */
export function ltx23ProPriceKey(res: string): '1080p' | '1440p' | '2160p' {
  if (res === '' || res === '720p' || res === '1080p') return '1080p';
  if (res === '1440p') return '1440p';
  if (res === '4k' || res.includes('2160') || res.includes('4k') || res.includes('4K')) return '2160p';
  return '1080p';
}

/** falai.ts:1636-1646 (the editor arm; the >10 s downgrade cannot fire — Pro clamps at 10 s). */
export function ltx23ProEditorWire(res: string): '1080p' | '1440p' | '2160p' {
  if (res === '720p' || res === '1080p') return '1080p';
  if (res === '1440p') return '1440p';
  if (res === '2160p' || res === '4k') return '2160p';
  return '1080p';
}

/** ProviderService.ts:1499-1502 (the headless arm), over `String(resolution ?? '')`. */
export function ltx23ProHeadlessWire(res: string): '1080p' | '1440p' | '2160p' {
  if (res === '1440p') return '1440p';
  if (res === '2160p' || res.includes('2160') || res.toLowerCase().includes('4k')) return '2160p';
  return '1080p';
}

/**
 * The four canonical values carry the four distinct (price, editor wire, headless wire) triples:
 *   '1080p' 37/1080p/1080p   '1440p' 74/1440p/1440p
 *   '2160p' 148/2160p/2160p  '4K'    148/1080p/2160p   <- every "spelled 4K-ish but not exactly
 *                                                          '2160p' or '4k'" string lands here
 */
export const LTX23_PRO_CANON = ['1080p', '1440p', '2160p', '4K'] as const;
export type Ltx23ProCanon = (typeof LTX23_PRO_CANON)[number];

/** The canonical value that behaves identically to `res` on all three readings, if one exists. */
export function ltx23ProCanonical(res: string): Ltx23ProCanon | undefined {
  const p = ltx23ProPriceKey(res);
  const e = ltx23ProEditorWire(res);
  const h = ltx23ProHeadlessWire(res);
  return LTX23_PRO_CANON.find((c) => ltx23ProPriceKey(c) === p && ltx23ProEditorWire(c) === e && ltx23ProHeadlessWire(c) === h);
}

/**
 * The derived alias map: every corpus spelling that is not already its own canonical value.
 * Spellings whose canonical value IS '1080p' are included too — they are identical to the
 * `invalid: {coerce:{kind:'default'}}` fallback, so they change nothing, but they put the
 * spelling into pricingParity's domain, which is the point (defect 1: "so the corpus stops
 * being the specification" — here the corpus is only the VERIFIED part of it).
 */
export function ltx23ProAliases(): Record<string, Ltx23ProCanon> {
  const out: Record<string, Ltx23ProCanon> = {};
  for (const s of RESOLUTION_SPELLINGS) {
    const canon = ltx23ProCanonical(s);
    if (canon !== undefined && canon !== s) out[s] = canon;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Hunyuan 1.5: the editor's post-reserve refusal, derived from the SHARED ladder
// ---------------------------------------------------------------------------

/**
 * falai.ts:1880-1900 refuses (after reserving, then refunds) any resolution the generic ladder
 * prices above 1x on a model whose payload carries no resolution field. The predicate is the
 * SHARED `legacy-resolution-v1` multiplier — not a spelling list — so the descriptor derives its
 * list from `legacyResolutionMultiplier` over the corpus instead of enumerating by hand.
 */
export function aboveOneXLadder(): string[] {
  return RESOLUTION_SPELLINGS.filter((s) => legacyResolutionMultiplier(s) > 1);
}

/** Re-exported so the family tests assert against the same function the ladder rate uses. */
export { getResolutionTier, legacyResolutionMultiplier };
